import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { AppConfigService, DOCUMENT, WINDOW, WindowService } from '@core/index';
import { registerLogFullStory } from '@shared/ui/utils/global-utils/full-story';
import { BookerFacade } from '@store/booker/booker.facade';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  fromEvent,
  map,
  merge,
  Subject,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import {
  EMContainer,
  EMDataLayer,
  EMIFrameStatus,
} from './every-mundo-container.interface';

export const ADNETIFY_SCRIPT = '__ADNETIFY_SCRIPT';

@Component({
  selector: 'jb-every-mundo-container',
  templateUrl: './every-mundo-container.component.html',
  styleUrls: ['./every-mundo-container.component.scss'],
})
export class EveryMundoComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('emContainer', { static: true }) emContainer: ElementRef;
  public content: EMContainer;
  public iframeStatus$ = new BehaviorSubject<EMIFrameStatus>('loading');
  private _mutationObserver: MutationObserver;
  public scriptElement: HTMLScriptElement;
  public isDestroyed$ = new Subject<void>();
  public ngStyles: {
    opacity: string;
    backgroundColor: string;
    minHeight: string;
  };

  constructor(
    public appConfigService: AppConfigService,
    public bookerFacade: BookerFacade,
    public renderer: Renderer2,
    public windowService: WindowService,
    public changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) public document: Document,
    @Inject(WINDOW) public window: Window,
  ) {}

  public ngOnInit(): void {
    combineLatest([
      this.iframeStatus$.pipe(
        tap(status => {
          if (status === 'error') {
            registerLogFullStory(
              'Everymundo Iframe Failure',
              {
                page_path_str: this.window.location.href,
                error_message_str: `Error Loading EM. Container Container ID: ${this.content.contentId}`,
              },
              this.window,
            );
          }
        }),
      ),
      this.windowService.isMobile,
      this.windowService.isTablet,
    ])
      .pipe(
        takeUntil(this.isDestroyed$),
        tap(([status, isMobile, isTablet]) => {
          this.updateNgStyle(status, isMobile, isTablet);
        }),
      )
      .subscribe();
    /*
    The app will face performance issues if the same container is loaded twice.
    If the content author add two containers ID with the same value by mistake, this logic will help to avoid performance issues in prod.
    By doing it, this duplicated container will not load.
    */
    const containerClassList = this.document.getElementsByClassName(
      this.content.contentId,
    );
    containerClassList.length >= 1 && (this.content.contentId = null);
  }

  public ngAfterViewInit(): void {
    // Create a EM Div with data-container-id.
    const newElement = this.renderer.createElement('div');

    // Set the data-container-id attribute to the new element
    if (this.content.contentId) {
      this.renderer.setAttribute(
        newElement,
        'data-container-id',
        this.content.contentId,
      );
      // Append the new element to the emContainer
      this.renderer.appendChild(this.emContainer.nativeElement, newElement);
    }
  }

  public onHtmlReady(): void {
    this.bookerFacade
      .currentOrigin$()
      .pipe(
        take(1),
        map(currentOrigin => {
          this._createDOMObserver();
          const originLocations: string[] = [];
          originLocations.push(currentOrigin.code);
          if (currentOrigin.cc) {
            currentOrigin.cc.forEach(cc => {
              originLocations.push(cc);
            });
          }
          return originLocations;
        }),
        tap(originLocations => {
          this.loadScript(originLocations);
        }),
      )
      .subscribe();
  }

  public loadScript(originLocations: string[]): void {
    const emScript = this.document.getElementById('em-container');

    if (!emScript) {
      const dataLayer: EMDataLayer[] = [
        {
          languageIsoCode: 'en',
          homeLocation: null,
          originLocations,
          destinationLocations: null,
        },
      ];
      this.window['MMdataLayer'] = dataLayer;
      this.scriptElement = this.renderer.createElement('script');
      this.scriptElement.src = this.appConfigService.emScriptUrl;
      this.scriptElement.id = 'em-container';

      this.renderer.appendChild(this.document.body, this.scriptElement);
    }
  }

  public handleIframeNode(iframe: HTMLIFrameElement): void {
    this._mutationObserver?.disconnect();
    const load$ = fromEvent(iframe, 'load').pipe(
      delay(0),
      map(() => {
        this.updateNgStyle('loaded', undefined, undefined, iframe.style.height);
        if (iframe.clientHeight > 1) {
          return 'loaded';
        } else {
          return 'error';
        }
      }),
    );

    const error$ = fromEvent(iframe, 'error').pipe(
      map(() => 'error' as EMIFrameStatus),
    );

    merge(load$, error$)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(event => {
        this.iframeStatus$.next(event);
      });
  }

  public updateNgStyle(
    status: EMIFrameStatus,
    isMobile?: boolean,
    isTablet?: boolean,
    overrideMinHeight?: string,
  ): void {
    let minHeight: string = overrideMinHeight;
    if (!overrideMinHeight) {
      if (isMobile) {
        minHeight = `${this.content.placeholderHeight.mobile}px`;
      } else if (isTablet) {
        minHeight = `${this.content.placeholderHeight.tablet}px`;
      } else {
        minHeight = `${this.content.placeholderHeight.desktop}px`;
      }
    }

    this.ngStyles = {
      minHeight: minHeight,
      backgroundColor:
        status === 'loading' ? this.content.placeholderColor : 'transparent',
      opacity: status === 'loading' ? this.content.placeholderOpacity : '1',
    };

    if (status !== 'loading') {
      this.changeDetectorRef.detectChanges();
    }
  }

  private _createDOMObserver(): void {
    this._mutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach((node: any) => {
          if (
            node.nodeName.toLowerCase() === 'iframe' &&
            node.id === `mm${this.content.contentId}`
          ) {
            this.handleIframeNode(node as HTMLIFrameElement);
          }
        });
      });
    });
    this._mutationObserver.observe(this.emContainer.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  public ngOnDestroy(): void {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
    this._mutationObserver?.disconnect();

    if (this.scriptElement) {
      this.renderer.removeChild(this.document.body, this.scriptElement);
      if (this.window?.[ADNETIFY_SCRIPT]) {
        this.window[ADNETIFY_SCRIPT] = null;
      }
    }
  }
}

export const everyMundoResolver = (_, data) => {
  const content: EMContainer = {
    ...data.emContainer?.[0],
    placeholderHeight: data.emContainer?.[0]?.placeholderHeight?.[0],
  };
  return {
    content,
  };
};
